import { Auth, Storage as S3 } from "aws-amplify";
import { Component, FunctionComponent } from "react";
import { CognitoUserSession } from "amazon-cognito-identity-js";
import {
    Col,
    Container,
    Row,
    Card,
    Alert,
    ListGroup,
    Nav,
    Dropdown,
    Tabs,
    Tab,
    Badge,
    Accordion,
    Table,
    Modal,
} from "react-bootstrap";
import { RouteComponentProps } from "react-router-dom";
import { OrderItem, IGetOrders, IUpdateOrderStatus, Order, OrderStatus, OrderType, AmendOrder as AmendOrderAPI, OrderCollectionDate } from "../../client/core";
import { Color, Path, Storage } from "../../env";
import { CSVLink } from "react-csv";
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, icon, variant } from "../form/Button";
import AmendOrder, { AmendOrderView } from "./AmendOrder";

export interface ListOrdersState {
    day: string;
    collectionDate: string;
    deliveryDays: string[];
    collectionDates: string[];
    deliveryOrders: Map<string, Map<string, Order[]>>;
    collectionOrders: Map<string, Order[]>;
    collectionOrdersCSVData: CSVData;
    deliveryOrdersCSVData: CSVData;
    activeAmendOrder?: Order;
    error: string;
}

export interface ListOrdersProps extends RouteComponentProps {
    coreAPI: IGetOrders;
    updateOrderStatusAPI: IUpdateOrderStatus;
    auth: typeof Auth;
}

export default class ListOrders extends Component<
    ListOrdersProps,
    ListOrdersState
> {
    constructor(props: ListOrdersProps) {
        super(props);
        this.state = {
            day: "",
            collectionDate: "Today",
            deliveryDays: [],
            collectionDates: [],
            deliveryOrders: new Map(),
            deliveryOrdersCSVData: {
                data: [],
            },
            error: "",
            collectionOrders: new Map(),
            collectionOrdersCSVData: {
                data: [],
            },
        };
    }

    handleDayChange = (day: string): void => {
        this.setState({
            day: day,
        })
    }

    componentDidMount() {
        this.getOrders();
    }

    logOut = (): void => {
        localStorage.removeItem(Storage.ShopID);
        this.props.auth.signOut();
        localStorage.removeItem(Storage.IsLoggedIn);
        return this.props.history.push(Path.Login);
    };

    getSession = async (): Promise<CognitoUserSession | Error> => {
        try {
            const session = await this.props.auth.currentSession();
            return session;
        } catch (err) {
            return new Error("could not retrieve user session");
        }
    };

    updateOrderStatus = async (status: OrderStatus, order: Order): Promise<void> => {
        const session = await this.getSession();
        if (session instanceof Error) {
            return this.logOut();
        }

        const shopID = localStorage.getItem(Storage.ShopID)
        if (!shopID) {
            return this.logOut();
        }

        const response = await this.props.updateOrderStatusAPI.UpdateOrderStatus({
            shopID: shopID,
            identityToken: session.getIdToken().getJwtToken(),
            paymentID: order.paymentId!,
            status: status,
        })

        if (response instanceof Error) {
            return this.setState({
                error: "We could not update the order status."
            })
        }

        if (response.statusCode === 403) {
            return this.logOut();
        }

        if (order.type === "collection" || !order.deliveryDate) {
            const today = new Date()
            const stateOrders = this.state.collectionOrders
            if (!order.collectionDate || (order.collectionDate.dom == today.getDate() && order.collectionDate.month == (today.getMonth() + 1) && order.collectionDate.year == today.getFullYear())) {
                stateOrders.get("Today")?.map(o => {
                    if (o.paymentId == order.paymentId) {
                        o.status = status
                        return o
                    }
                    return o
                })
                return this.setState({
                    collectionOrders: stateOrders
                })
            }

            const date: string = this.collectionDateToString(order.collectionDate)
            stateOrders.get(date)?.map(o => {
                if (o.paymentId == order.paymentId) {
                    o.status = status
                    return o
                }
                return o
            })
            return this.setState({
                collectionOrders: stateOrders
            })
        }

        const timeIndex = order.deliveryDate.time.start + "-" + order.deliveryDate.time.end
        const orders = this.state.deliveryOrders.get(order.deliveryDate.day)?.get(timeIndex)!.map(o => {
            if (o.paymentId == order.paymentId) {
                o.status = status
                return o
            }
            return o
        })!
        const stateOrders = this.state.deliveryOrders
        stateOrders.get(order.deliveryDate.day)?.set(timeIndex, orders)
        this.setState({
            deliveryOrders: stateOrders,
        })
    }

    getOrders = async (): Promise<void> => {
        const session = await this.getSession();
        if (session instanceof Error) {
            return this.logOut();
        }

        const shopID = localStorage.getItem(Storage.ShopID);
        if (!shopID) {
            return;
        }

        const response = await this.props.coreAPI.GetOrders({
            shopID: shopID,
            identityToken: session.getIdToken().getJwtToken(),
        });
        if (response instanceof Error) {
            this.setState({
                error: response.message,
            });
            return;
        }

        if (response.statusCode == 403) {
            return this.logOut();
        }

        if (response.statusCode != 201) {
            this.setState({
                error: "Could not retrieve orders. Please try again shorty.",
            });
        }

        const deliveryOrders: Map<string, Map<string, Order[]>> = this.marshalDeliveryOrders(response.response?.orders)
        const collectionOrders: Map<string, Order[]> = this.marshalCollectionOrders(response.response?.orders)
        const deliveryDays: string[] = this.marshalDeliveryDays(response.response?.orders || [])
        const collectionDates: string[] = this.marshalCollectionDays(response.response?.orders || [])

        this.setState({
            day: (deliveryDays.length > 0) ? deliveryDays[deliveryDays.length - 1] : "",
            error: "",
            deliveryOrders: deliveryOrders,
            collectionOrders: collectionOrders,
            deliveryOrdersCSVData: this.mapDeliveryOrdersToCSV(response.response?.orders),
            collectionOrdersCSVData: this.mapCollectionOrdersToCSV(response.response?.orders),
            deliveryDays: deliveryDays,
            collectionDates: collectionDates,
        });
    };

    marshalCollectionDays = (orders: Order[]): string[] => {
        const sortedOrders = orders.filter(order => order.type == "collection").sort((a, b) => {
            if ((a.collectionDate?.year || 0) > (b.collectionDate?.year || 0)) {
                return 1
            }
            if ((a.collectionDate?.month || 0) > (b.collectionDate?.month || 0)) {
                return 1
            }
            if ((a.collectionDate?.dom || 0) > (b.collectionDate?.dom || 0)) {
                return 1
            }
            return -1
        })

        return Array.from(new Set(sortedOrders.map(order => String(this.collectionDateToString(order.collectionDate)))))
    }

    marshalDeliveryDays = (orders: Order[]): string[] => {
        return Array.from(new Set(orders.sort((a, b) => ((a.timestamp || 0) > (b.timestamp || 0)) ? 1 : -1).filter(order => order.type == "delivery").map(order => String(order.deliveryDate?.day))))
    }

    marshalCollectionOrders = (orders: Order[] | undefined): Map<string, Order[]> => {
        const response: Map<string, Order[]> = new Map();
        if (!orders) {
            return response
        }

        const collectionOrder = orders.filter(o => o.type == "collection")
        for (const order of collectionOrder) {
            if (this.isCollectionNow(order.collectionDate)) {
                if (!response.get("Today")) {
                    response.set("Today", [])
                }
                response.get("Today")?.push(order)
                continue
            }

            const date: string = this.collectionDateToString(order.collectionDate)

            if (!response.get(date)) {
                response.set(date, [])
            }
            response.get(date)?.push(order)
        }

        return response
    }

    isCollectionNow = (date: OrderCollectionDate | undefined): boolean => {
        const today = new Date()
        if (!date) {
            return true
        }
        if (date.dom <= today.getDate() && date.month <= (today.getMonth() + 1) && date.year <= today.getFullYear()) {
            return true
        }
        return false
    }

    collectionDateToString = (date: OrderCollectionDate | undefined): string => {
        if (this.isCollectionNow(date)) {
            return "Today"
        }

        const d = new Date(date!.year, date!.month - 1, date!.dom)
        const dayWithSuffix = this.getDateSuffix(date!.dom)
        const weekday = d.toLocaleDateString('en-GB', { weekday: 'long' });

        return `${weekday} ${dayWithSuffix}`
    }

    getDateSuffix(day: number): string {
        if (day >= 11 && day <= 13) return `${day}th`;
        switch (day % 10) {
            case 1: return `${day}st`;
            case 2: return `${day}nd`;
            case 3: return `${day}rd`;
            default: return `${day}th`;
        }
    }

    marshalDeliveryOrders = (orders: Order[] | undefined): Map<string, Map<string, Order[]>> => {
        const response: Map<string, Map<string, Order[]>> = new Map();
        if (!orders) {
            return response
        }

        for (const order of orders) {
            if (order.type !== "delivery" || !order.deliveryDate) {
                continue;
            }
            if (!response.get(order.deliveryDate.day)) {
                response.set(order.deliveryDate.day, new Map<string, Order[]>())
            }
            const o: Map<string, Order[]> = response.get(order.deliveryDate.day)!
            let timeOrders = o.get(order.deliveryDate.time.start + "-" + order.deliveryDate.time.end)
            if (!timeOrders) {
                timeOrders = []
            }
            timeOrders.push(order)
            o.set(order.deliveryDate.time.start + "-" + order.deliveryDate.time.end, timeOrders)
            response.set(order.deliveryDate.day, o)
        }

        return response
    }

    mapDeliveryOrdersToCSV = (orders: Order[] | undefined): CSVData => {
        const response: CSVData = {
            data: [],
            headers: [
                {
                    label: "Order Id",
                    key: "orderId"
                },
                {
                    label: "Delivery Date",
                    key: "deliveryDate"
                },
                {
                    label: "Delivery Details",
                    key: "deliveryDetails"
                },
                {
                    label: "Address",
                    key: "address"
                },
                {
                    label: "Items",
                    key: "items"
                },
                {
                    label: "Total",
                    key: "total"
                },
                {
                    label: "Delivered",
                    key: "delivered",
                }
            ],
            filename: "Deliveries.csv"
        }

        if (!orders) {
            return response;
        }

        const marshalledOrders: Map<string, Map<string, Order[]>> = this.marshalDeliveryOrders(orders)

        for (const day of Array.from(marshalledOrders.keys())) {
            for (const time of Array.from(marshalledOrders.get(day)!.keys())) {
                for (const order of marshalledOrders.get(day)!.get(time)!) {
                    response.data.push({
                        deliveryDate: order.deliveryDate?.day,
                        orderId: order.paymentId!.substring(0, 10),
                        address: order.address,
                        deliveryDetails: order.deliveryDetails,
                        items: (order.items || []).map(item => `${item.name} ${item.volume || ''}${((item.uom || '').toLowerCase() == "unit") ? "" : item.uom || ""}`).join(',\n'),
                        total: `£${order.paymentTotal}`,
                        delivered: (order.status == OrderStatus.Delivered) ? "Yes" : ""
                    })
                }
            }
        }

        return response;
    }

    mapCollectionOrdersToCSV = (orders: Order[] | undefined): CSVData => {
        const response: CSVData = {
            headers: [
                {
                    label: "Order Date",
                    key: "orderDate"
                },
                {
                    label: "Order Id",
                    key: "orderId"
                },
                {
                    label: "Collection Date",
                    key: "collectionDate"
                },
                {
                    label: "Items",
                    key: "items"
                },
                {
                    label: "Total",
                    key: "total"
                },
                {
                    label: "Collected",
                    key: "collected",
                }
            ],
            data: [],
            filename: "All Collections.csv",
        }

        if (!orders) {
            return {
                data: []
            };
        }

        orders.filter(order => order.type == "collection").forEach(order => {
            response.data.push({
                orderDate: (order.created) ? new Date(order.created * 1000).toLocaleString() : undefined,
                orderId: order.paymentId!.substring(0, 10),
                collectionDate: (order.collectionDate && order.collectionDate.dom !== 0) ? `${order.collectionDate?.dom}/${order.collectionDate?.month}/${order.collectionDate?.year}` : "Not Specified",
                items: this.orderItemsToString(order.items),
                total: `£${order.paymentTotal}`,
                collected: (order.status != OrderStatus.ToBeCollected) ? "Yes" : ""
            })
        })

        return response;
    }

    orderItemsToString = (items: OrderItem[] | undefined): string => {
        if (!items) {
            return ""
        }
        let itemsString = ""
        items.forEach(item => {
            itemsString += `${item.name} (${item.volume} ${item.uom}), `
        })
        itemsString = itemsString.replace(/, $/, '');
        return itemsString
    }

    handleAmendOrderSubmit = (order: Order): void => {
        let orders: Order[]
        if (order.type == OrderType.OrderTypeCollection) {
            orders = this.state.collectionOrders.get(this.collectionDateToString(order.collectionDate)) || []
            orders = orders.map(o => {
                if (o.paymentId !== order.paymentId) {
                    return o
                }
                return order
            })
            return this.setState({
                collectionOrders: this.state.collectionOrders.set(this.collectionDateToString(order.collectionDate), orders)
            })
        }

        const timeKey = `${order.deliveryDate?.time.start}-${order.deliveryDate?.time.end}`
        orders = this.state.deliveryOrders.get(order.deliveryDate?.day!)!.get(timeKey)!
        if (!orders) {
            return
        }

        orders = orders.map(o => {
            if (o.paymentId !== order.paymentId) {
                return o
            }
            return order
        })
        const deliveryOrders = this.state.deliveryOrders
        const dayDeliveryOrders = deliveryOrders.get(order.deliveryDate?.day!)
        dayDeliveryOrders?.set(timeKey, orders)
        deliveryOrders.set(order.deliveryDate?.day!, dayDeliveryOrders!)
        this.setState({
            activeAmendOrder: undefined,
            deliveryOrders: deliveryOrders,
        })
    }

    render() {
        const props: ListOrdersViewProps = {
            ...this.props,
            deliveryOrderDays: this.state.deliveryDays,
            collectionOrders: this.state.collectionOrders,
            collectionOrderDates: this.state.collectionDates,
            collectionOrdersCSVData: this.state.collectionOrdersCSVData,
            day: this.state.day,
            collectionDate: this.state.collectionDate,
            deliveryOrders: this.state.deliveryOrders,
            deliveryOrdersCSVData: this.state.deliveryOrdersCSVData,
            error: this.state.error,
            activeAmendOrder: this.state.activeAmendOrder,
            auth: this.props.auth,
            onCollectionDateChange: date => this.setState({ collectionDate: date }),
            onDayChange: this.handleDayChange,
            onOrderStatusUpdate: this.updateOrderStatus,
            onAmendOrder: (order?: Order) => this.setState({ activeAmendOrder: order }),
            onAmendOrderSubmit: this.handleAmendOrderSubmit,
        };
        return <ListOrdersView {...props} />;
    }
}

export interface ListOrdersViewProps extends RouteComponentProps {
    deliveryOrderDays: string[];
    deliveryOrders: Map<string, Map<string, Order[]>>;
    collectionOrderDates: string[];
    collectionOrders: Map<string, Order[]>;
    deliveryOrdersCSVData: CSVData;
    collectionOrdersCSVData: CSVData;
    collectionDate: string;
    day: string;
    error: string;
    activeAmendOrder?: Order;
    auth: typeof Auth;
    onDayChange: (day: string) => void;
    onCollectionDateChange: (date: string) => void;
    onOrderStatusUpdate: (status: OrderStatus, order: Order) => void
    onAmendOrder: (order?: Order) => void;
    onAmendOrderSubmit: (order: Order) => void;
}

export interface CSVData {
    data: object[]
    headers?: any;
    separator?: string;
    filename?: string;
}

export const ListOrdersView: FunctionComponent<ListOrdersViewProps> = (
    props
) => (
    <>
        <Modal
            size="lg"
            show={props.activeAmendOrder !== undefined}
            onHide={() => props.onAmendOrder()}
        >
            <Modal.Header>
                <Row style={{ width: "100%" }}>
                    <Col>
                        <Modal.Title>Amend Order</Modal.Title>
                    </Col>
                    <Col style={{ textAlign: "right" }}>
                        <Button icon={icon.Close} buttonvariant={variant.Secondary} onClick={() => props.onAmendOrder()} />
                    </Col>
                </Row>
            </Modal.Header>
            <Modal.Body>
                {(props.activeAmendOrder) && <AmendOrder {...props} onCancel={() => props.onAmendOrder()} postSubmit={(order) => props.onAmendOrderSubmit(order)} auth={props.auth} amendOrderAPI={new AmendOrderAPI()} order={props.activeAmendOrder} />}
            </Modal.Body>
        </Modal>
        <Alert variant={"danger"} show={props.error != ""}>
            {props.error}
        </Alert>
        <h1 style={{ marginBottom: "2rem" }}>Orders</h1>
        <div style={{ marginTop: "1rem" }}>
            <Tabs
                defaultActiveKey={OrderType.OrderTypeDelivery}
                className="mb-3"
                variant="pills"
            >
                <Tab eventKey={OrderType.OrderTypeDelivery} title="Deliveries">
                    {(props.deliveryOrders && props.deliveryOrders.size > 0) && (
                        <>
                            <Row>
                                <Col xs={12} sm={9}>
                                    {props.deliveryOrders.has(props.day) && (
                                        <DaySelector activeDay={props.day} days={props.deliveryOrderDays} {...props} />
                                    )}
                                </Col>
                                <Col xs={12} sm={3} style={{ textAlign: "right" }}>
                                    <Dropdown>
                                        <Dropdown.Toggle style={{ background: Color.Primary, border: 0, borderRadius: 0 }} id="actions">
                                            Actions
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <CSVLink
                                                className="dropdown-item"
                                                data={props.deliveryOrdersCSVData.data}
                                                headers={props.deliveryOrdersCSVData.headers}
                                                filename={props.deliveryOrdersCSVData.filename}
                                            >
                                                Export to CSV
                                            </CSVLink>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </Col>
                            </Row>
                            {props.deliveryOrders.has(props.day) && (
                                <>
                                    {Array.from(props.deliveryOrders.get(props.day)!.keys()).map((time) => (
                                        <div key={`order-time-board-${props.day}-${time}`} style={{ marginBottom: "1rem", marginTop: "1rem" }}>
                                            <h6 style={{ textAlign: "center", padding: "1rem", borderBottom: `.5px solid ${Color.Grey}` }}>Delivery Time: {time}</h6>
                                            <Row>
                                                <Col>
                                                    <Row style={{ borderRight: "1px dashed lightgrey" }}>
                                                        <OrdersView
                                                            title="To Be Delivered"
                                                            status={OrderStatus.ToBeDelivered}
                                                            orders={props.deliveryOrders.get(props.day)!.get(time)!.filter(o => o.status == OrderStatus.ToBeDelivered && o.type == OrderType.OrderTypeDelivery)}
                                                            onClick={order => props.onOrderStatusUpdate(OrderStatus.Delivered, order)}
                                                            onAmend={props.onAmendOrder}
                                                        />
                                                    </Row>
                                                </Col>
                                                <Col>
                                                    <Row style={{ borderLeft: "1px dashed lightgrey" }}>
                                                        <OrdersView
                                                            title="Delivered"
                                                            status={OrderStatus.Delivered}
                                                            orders={props.deliveryOrders.get(props.day)!.get(time)!.filter(o => o.status != OrderStatus.ToBeDelivered && o.type == OrderType.OrderTypeDelivery)}
                                                            onClick={order => props.onOrderStatusUpdate(OrderStatus.ToBeDelivered, order)}
                                                            onAmend={props.onAmendOrder}
                                                        />
                                                    </Row>
                                                </Col>
                                            </Row>
                                        </div>
                                    ))}
                                </>
                            )}
                        </>
                    )}
                </Tab>

                <Tab eventKey={OrderType.OrderTypeCollection} title="Collections">
                    {(props.collectionOrders && props.collectionOrders.size > 0) && (
                        <>
                            <Row>
                                <Col xs={12} sm={9}>
                                    <DaySelector activeDay={props.collectionDate} days={props.collectionOrderDates} onDayChange={props.onCollectionDateChange} />
                                </Col>
                                <Col xs={12} sm={3} style={{ textAlign: "right" }}>
                                    <Dropdown>
                                        <Dropdown.Toggle style={{ background: Color.Primary, border: 0, borderRadius: 0 }} id="actions">
                                            Actions
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <CSVLink
                                                className="dropdown-item"
                                                data={props.collectionOrdersCSVData.data}
                                                headers={props.collectionOrdersCSVData.headers}
                                                filename={props.collectionOrdersCSVData.filename}
                                            >
                                                Export to CSV
                                            </CSVLink>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Row style={{ borderRight: "1px dashed lightgrey" }}>
                                        <OrdersView
                                            title="To Be Collected"
                                            status={OrderStatus.ToBeCollected}
                                            orders={(props.collectionOrders.get(props.collectionDate) || []).filter(o => o.status == OrderStatus.ToBeCollected)}
                                            onClick={order => props.onOrderStatusUpdate(OrderStatus.Delivered, order)}
                                            onAmend={props.onAmendOrder}
                                        />
                                    </Row>
                                </Col>
                                <Col>
                                    <Row style={{ borderLeft: "1px dashed lightgrey" }}>
                                        <OrdersView
                                            title="Collected"
                                            status={OrderStatus.Delivered}
                                            orders={(props.collectionOrders.get(props.collectionDate) || []).filter(o => o.status != OrderStatus.ToBeCollected)}
                                            onClick={order => props.onOrderStatusUpdate(OrderStatus.ToBeCollected, order)}
                                            onAmend={props.onAmendOrder}
                                        />
                                    </Row>
                                </Col>
                            </Row>
                        </>
                    )}
                </Tab>
            </Tabs>
            {((props.deliveryOrders.size === 0 && props.collectionOrders.size === 0) && props.error.trim() == "") && (
                <div style={{ marginTop: ".2rem", padding: "0 2rem" }}>
                    <FontAwesomeIcon color={Color.Primary} style={{ display: "inline-block", fontSize: "1.5rem", marginRight: "1rem" }} icon={faCircleCheck} />
                    <Card.Text style={{ display: "inline-block" }}>You currently have no orders</Card.Text>
                </div>
            )}
        </div>
    </>
);

export interface OrdersViewProps {
    title: string
    status: OrderStatus
    orders: Order[]
    onClick: (order: Order) => void
    onAmend: (order?: Order) => void
}

export const OrdersView: FunctionComponent<OrdersViewProps> = props => (
    <div style={{ padding: "1rem", display: "block" }}>
        <h6>{props.title}</h6>
        <Accordion>
            {props.orders.map((order) => (
                <Row style={{ marginBottom: ".5rem" }} key={`${order.paymentId}-order`}>
                    {props.status == OrderStatus.Delivered && (
                        <Col xs={4} md={2}>
                            <Button
                                style={{ width: "100%", height: "100%" }}
                                icon={icon.LeftArrow}
                                buttonvariant={variant.Danger}
                                onClick={() => props.onClick(order)} />
                        </Col>
                    )}
                    <Col xs={8} md={10}>
                        <Accordion.Item style={{ width: "100%", display: "inline-block" }} eventKey={order.paymentId!}>
                            <Accordion.Header>
                                <Row style={{ width: "100%", marginRight: ".2rem", marginLeft: ".2rem" }}>
                                    <Col>
                                        <span>Order: {order.paymentId!.substring(0, 10)}</span>
                                    </Col>
                                    <Col style={{ textAlign: "right" }}>
                                        <span style={{ fontWeight: 700 }}>Total: £{Number(order.paymentTotal).toFixed(2)}</span>
                                    </Col>
                                </Row>
                            </Accordion.Header>
                            <Accordion.Body>
                                <Table>
                                    <tbody>
                                        <tr>
                                            <td>Actions</td>
                                            <td><Button onClick={() => props.onAmend(order)} buttonvariant={variant.Primary} name="Amend" /></td>
                                        </tr>
                                        <tr>
                                            <td>ID</td>
                                            <td>{order.customerId}</td>
                                        </tr>
                                        <tr>
                                            <td>Email</td>
                                            <td>{order.customerEmail}</td>
                                        </tr>
                                        <tr>
                                            <td>Address</td>
                                            <td>{order.address}</td>
                                        </tr>
                                        {(order.created) && <tr>
                                            <td>Time of order</td>
                                            <td>{new Date(order.created * 1000).toLocaleString()}</td>
                                        </tr>}
                                        <tr>
                                            <td>Details</td>
                                            {(!order.deliveryDetails) && (<td>None</td>)}
                                            {(order.deliveryDetails) && (<td>{order.deliveryDetails.split(",").map(detail => <li style={{ listStyleType: "none" }}>{detail}</li>)}</td>)}
                                        </tr>
                                        <tr>
                                            <td>Items</td>
                                            <td><ListItemsView items={order.items || []} /></td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Col>
                    {props.status == OrderStatus.ToBeDelivered && (
                        <Col xs={4} md={2}>
                            <Button
                                data-testid={`${order.paymentId}-to-be-delivered`}
                                style={{ width: "100%", height: "100%" }}
                                icon={icon.RightArrow}
                                buttonvariant={variant.Primary}
                                onClick={() => props.onClick(order)} />
                        </Col>
                    )}
                    {props.status == OrderStatus.ToBeCollected && (
                        <Col xs={4} md={2}>
                            <Button
                                style={{ width: "100%", height: "100%" }}
                                icon={icon.RightArrow}
                                buttonvariant={variant.Primary}
                                onClick={() => props.onClick(order)} />
                        </Col>
                    )}

                </Row>
            ))}
        </Accordion >
    </div >
)

export interface DaySelectorProps {
    activeDay: string
    days: string[]
    onDayChange: (day: string) => void;
}

export const DaySelector: FunctionComponent<DaySelectorProps> = props => (
    <Nav className="me-auto">
        {props.days.map(day => (props.activeDay === day) ? (
            <Nav.Link key={`${day}-day-selector-link`} style={{ background: Color.Primary, padding: 10, color: Color.White, fontWeight: "bold" }} onClick={() => props.onDayChange(day)}>{day}</Nav.Link>
        ) : (

            <Nav.Link key={`${day}-day-selector-link`} style={{ color: Color.Primary, fontWeight: 600 }} onClick={() => props.onDayChange(day)}>{day}</Nav.Link>
        ))}
    </Nav>
)

export interface ListItemsViewProps {
    items: OrderItem[];
}

export const ListItemsView: FunctionComponent<ListItemsViewProps> = (props) => (
    <ListGroup style={{ width: "100%" }}>
        {props.items &&
            props.items.map((item, key) => (
                <ListGroup.Item
                    key={`${item.id}-list-item`}
                    as="li"
                    style={{ width: "100%" }}
                >
                    <div className="ms-2">
                        <div className="fw-bold">{item.name}</div>
                        <div className="fw-bold" style={{ float: "right" }}>£{Number(item.price).toFixed(2)}</div>
                    </div>
                    {item.variants && item.variants.map(variant => (
                        <Badge bg={"secondary"} style={{ color: Color.White, padding: ".5rem", margin: ".1rem" }}>{variant.name}: {variant.value}</Badge>
                    ))}
                    <Badge bg={"secondary"} style={{ color: Color.White, padding: ".5rem", margin: ".1rem" }}>{item.volume + " " + item.uom} {((item.volume && item.volume > 1) && (!item.uom || item.uom == "Unit")) ? "s" : ""}</Badge>
                </ListGroup.Item>
            ))}
    </ListGroup>
);